import React, { useEffect, useRef, useState } from 'react';
import { either, isNil, isEmpty, dec, inc, compose, length, defaultTo, nth, find } from 'ramda';
import { Button } from 'antd';
import { EyeOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import DocumentWrapper from './DocumentWrapper';
import { resolveWidgetDivFromEvent } from './helpers/resolveWidgetAtPoint';

const isNilOrEmpty = either(isNil, isEmpty);

const HIGHLIGHT_BACKGROUND = 'rgba(24, 144, 255, 0.08)';

const PaginationButton = ({ disabled, children, onClick, className, hasToBeOnSameSection }) => {
  return (
    <Button
      className={classNames(
        className,
        { 'w-9 !h-9 !bg-white rounded-md border border-solid !border-flexi-select-active': !hasToBeOnSameSection },
        { '!border-none !bg-flexi-select-active px-2': hasToBeOnSameSection },
        { 'text-flexi-grey cursor-default': disabled },
        { 'text-flexi-primary-color cursor-pointer': !disabled && !hasToBeOnSameSection },
        { 'text-black cursor-pointer': !disabled && hasToBeOnSameSection }
      )}
      onClick={onClick}
      disabled={disabled}
      type="text"
    >
      {children}
    </Button>
  );
};

const Pagination = ({
  numberOfPages,
  currentPageIndex,
  setCurrentPageIndex,
  hasToBeOnSameSection
}) => {
  if (numberOfPages === 0) {
    return null;
  }

  return (
    <div className={classNames('flex items-center mr-4', { 'gap-x-0': hasToBeOnSameSection }, { 'gap-x-2': !hasToBeOnSameSection })}>
      <PaginationButton
        className={`previous-section-button ${hasToBeOnSameSection && '!rounded-none !rounded-l-2xl'}`}
        disabled={currentPageIndex <= 0}
        onClick={() => setCurrentPageIndex(dec)}
        hasToBeOnSameSection={hasToBeOnSameSection}
      >
        <LeftOutlined/>
      </PaginationButton>

      <PaginationButton
        className={`next-section-button ${hasToBeOnSameSection && '!rounded-none !rounded-r-2xl !mr-2'}`}
        disabled={currentPageIndex >= (numberOfPages - 1)}
        onClick={() => setCurrentPageIndex(inc)}
        hasToBeOnSameSection={hasToBeOnSameSection}
      >
        <RightOutlined/>
      </PaginationButton>

      <span className="section-number font-semibold">{!hasToBeOnSameSection && 'Section '}{currentPageIndex + 1}{hasToBeOnSameSection && `/${numberOfPages}`}</span>
    </div>
  );
};

const PreviewDocument = ({
  documentPreviewUrl,
  pagesHtml,
  previewDocumentHtml,
  previewHtmlLoading,
  classNameOrientationDocument,
  pageHeightDocument,
  activeKey,
  hasToBeOnSameSection,
  elementsToAdd,
  isModel,
  onWidgetEdit,
  onWidgetHighlight
}) => {
  const getNumberOfPages = compose(
    length,
    defaultTo([])
  );

  const [numberOfPages, setNumberOfPages] = useState(getNumberOfPages(pagesHtml));
  const [currentPageIndex, setCurrentPageIndex] = useState(Number(activeKey));

  useEffect(() => {
    setNumberOfPages(getNumberOfPages(pagesHtml));
    setCurrentPageIndex(Number(activeKey));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagesHtml, activeKey]);

  const currentPageHtml = nth(currentPageIndex, defaultTo([], pagesHtml));

  const previewerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null); // { name, x, y }

  useEffect(() => {
    const container = previewerRef.current;
    if (!container || !currentPageHtml) return;

    // On rows containing several widgets, the [data-position] divs do not match
    // the visible content (empty grid areas overlapping each other). The widget
    // is therefore resolved from the stack of elements under the cursor
    // (elementsFromPoint), favoring actual content, rather than through
    // listeners attached to the widget divs (whose topmost may be the wrong
    // widget).
    let hoveredDiv = null;

    const clearHover = () => {
      if (!hoveredDiv) return;
      hoveredDiv.style.background = '';
      hoveredDiv.style.cursor = '';
      hoveredDiv = null;
      setTooltip(null);
    };

    const handleMouseMove = (e) => {
      const div = resolveWidgetDivFromEvent(e);
      if (div === hoveredDiv) return;
      clearHover();
      if (!div) return;
      hoveredDiv = div;
      div.style.background = HIGHLIGHT_BACKGROUND;
      div.style.cursor = 'pointer';
      const name = div.getAttribute('data-widget-name');
      const rect = div.getBoundingClientRect();
      setTooltip({
        name, x: rect.left, y: rect.top - 28
      });
    };

    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseleave', clearHover);

    return () => {
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseleave', clearHover);
      clearHover();
    };
  }, [currentPageHtml]);

  const handleWidgetDivClick = (e) => {
    // Late-edit mode: the preview HTML carries no data-position attribute, the
    // widget is identified by walking up from the clicked element (legacy behavior).
    if (onWidgetEdit) {
      let div = e.target;
      while (div && div !== e.currentTarget) {
        if (div.tagName === 'DIV') {
          const widgetId = find(className => /^[a-f0-9]{24}$/.test(className), div.classList);
          if (widgetId) { onWidgetEdit(widgetId); return; }
        }
        div = div.parentElement;
      }
      return;
    }

    const div = resolveWidgetDivFromEvent(e);
    if (!div) return;
    const widgetId = find(className => /^[a-f0-9]{24}$/.test(className), div.classList);
    const position = div.getAttribute('data-position');
    if (onWidgetHighlight && widgetId && position) { onWidgetHighlight(widgetId, position); }
  };

  return (
    <div style={{ position: 'relative' }}>
      {tooltip && (
        <div
          style={{
            position: 'fixed',
            left: tooltip.x,
            top: tooltip.y,
            background: 'rgba(0,0,0,0.75)',
            color: '#fff',
            padding: '2px 8px',
            borderRadius: 4,
            fontSize: 12,
            pointerEvents: 'none',
            zIndex: 9999,
            whiteSpace: 'nowrap'
          }}
        >
          {tooltip.name}
        </div>
      )}
      <div className={classNames('bg-white py-4 pl-8 flex items-center', { 'shadow-none justify-end': isModel }, { 'place-content-between': !isModel })}>
        {!hasToBeOnSameSection && <span className="text-xl uppercase text-flexi-text font-bold">Prévisualisation du document</span>}
        {elementsToAdd && elementsToAdd}
        <Pagination
          currentPageIndex={currentPageIndex}
          numberOfPages={numberOfPages}
          setCurrentPageIndex={setCurrentPageIndex}
          hasToBeOnSameSection={hasToBeOnSameSection}
        />
      </div>

      <div
        ref={previewerRef}
        className={classNames('document-previewer p-4', { '!h-[83vh] flex justify-center items-start': hasToBeOnSameSection }, { '!p-0': isModel }, { 'overflow-x-auto bg-flexi-col-table': !hasToBeOnSameSection })}
        // eslint-disable-next-line no-restricted-syntax
        style={{ height: pageHeightDocument }}
        onClick={(e) => (onWidgetEdit || onWidgetHighlight) && handleWidgetDivClick(e)}
      >
        {isNilOrEmpty(documentPreviewUrl) && isNilOrEmpty(currentPageHtml) && (
          <Button
            className="block mt-250px mx-auto mb-0"
            onClick={previewDocumentHtml}
            loading={previewHtmlLoading}
            icon={<EyeOutlined />}
            size="large"
          >
            Prévisualiser
          </Button>
        )}

        {!isNilOrEmpty(currentPageHtml) && (
          <div
            dangerouslySetInnerHTML={{ __html: currentPageHtml }}
            className={classNames(classNameOrientationDocument, { 'scale !shadow-none border': hasToBeOnSameSection })}
          />
        )}

        {!isNilOrEmpty(documentPreviewUrl) && (
          <DocumentWrapper documentUrl={documentPreviewUrl} setNumberOfPages={setNumberOfPages} numberOfPages={numberOfPages} />
        )}
      </div>
    </div>
  );
};

export default PreviewDocument;
